import { component } from 'picoapp';
import Swiper from 'swiper';
import { repaint } from '../../js/utils/node';
import { hide as hideOverlay, show as showOverlay } from '../../js/utils/overlay';

export default component(async (node, ctx) => {
  /**
   * Data + elements
   */
  const topbar = document.querySelector('.js-topbar');
  const topbarCloseBtn = topbar?.querySelector('.js-topbarCloseBtn');
  const megamenus = node.querySelectorAll('.js-megamenu');
  const megamenuTriggers = node.querySelectorAll('.js-megamenuTrigger');

  let previousScrollPos = window.scrollY;

  const headerHeightMobile = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height--mobile'), 10);
  const headerHeightDesktop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height--desktop'), 10);
  let topbarHeight = 0;

  /**
   * Functions
   */
  const initTopbar = () => {
    const closed = sessionStorage.getItem('topbar');

    // Update topbar height for use in handleScroll
    const updateHeight = () => {
      topbarHeight = topbar.getBoundingClientRect().height;
    };

    const close = () => {
      topbar.remove();
      window.dispatchEvent(new Event('resize'));

      if (!closed) {
        sessionStorage.setItem('topbar', 'closed');
      }
    };

    // Remove if already hidden
    if (closed) {
      close();
    }

    updateHeight();
    ctx.on('window:resize', updateHeight);
    topbarCloseBtn.addEventListener('click', close);

    const config = JSON.parse(topbar.dataset.config);
    const { productTitleMarkup } = config;

    if (productTitleMarkup) {
      // Create blueprint for title element
      const blueprint = document.createElement('span');
      blueprint.classList.add('whitespace-nowrap', 'js-productTitle');
      blueprint.innerHTML = productTitleMarkup;

      topbar.appendChild(blueprint);
      const blueprintWidth = Math.ceil(blueprint.getBoundingClientRect().width);
      topbar.removeChild(blueprint);

      const updateTitles = () => {
        // Clear existing titles
        const existingTitles = topbar.querySelectorAll('.js-productTitle');
        existingTitles.forEach((title) => {
          title.remove();
        });

        // Calculate number of titles to add
        const topbarWidth = topbar.getBoundingClientRect().width;
        const topbarPadding = parseInt(getComputedStyle(topbar).getPropertyValue('padding'), 10) || 0 * 2;
        const topbarFreeSpace = topbarWidth - topbarPadding;
        const textElementWidth = Math.ceil(topbar.querySelector('.js-text')?.getBoundingClientRect().width) || 0;
        const freeSpace = Math.ceil(topbarFreeSpace - textElementWidth);
        const numberOfTitlePairs = Math.floor(freeSpace / ((blueprintWidth + 20) * 2));

        // Add title elements
        for (let i = 0; i < numberOfTitlePairs; i += 1) {
          const leftClone = blueprint.cloneNode(true);
          const rightClone = blueprint.cloneNode(true);

          leftClone.style.marginRight = '20px';
          rightClone.style.marginLeft = '20px';

          topbar.insertAdjacentElement('afterbegin', leftClone);
          topbar.insertAdjacentElement('beforeend', rightClone);
        }
      };

      updateTitles();
      ctx.on('window:resize', updateTitles);
    }
  };

  const initMegamenus = () => {
    const showMegamenu = (trigger, megamenu) => {
      node.classList.add('header--megamenu-open');
      megamenu.hidden = false;
      repaint(megamenu);
      megamenu.classList.add('header__megamenu--active');

      const listItem = trigger.closest('.js-listItem');
      listItem.classList.add('header__nav__list-item--active');
      trigger.classList.add('open');
      if (megamenu.classList.contains('header__megamenu--active')) {
        showOverlay();
      }

      //close other menu
      const megamenuTriggers = document.querySelectorAll('.has-megamenu');
      const header = document.querySelector('.megamenu-header');

      megamenuTriggers.forEach(function (trigger) {
        const menuHandle = trigger.getAttribute('data-menu-handle');
        const megamenu = document.getElementById('megamenu-' + menuHandle);
        if (megamenu) {
          megamenu.classList.remove('show');
          header.classList.remove('design-white');
          megamenu.classList.remove('design-white');
          trigger.classList.remove('open');
        }
      });

    };

    const hideMegamenu = (trigger, megamenu) => {
      megamenu.classList.remove('header__megamenu--active');

      const transitionDuration = parseInt(getComputedStyle(megamenu).getPropertyValue('transition-duration') || 0, 10);

      if (transitionDuration === 0) {
        megamenu.hidden = true;
      } else {
        megamenu.addEventListener('transitionend', () => {
          if (!megamenu.classList.contains('header__megamenu--active')) {
            megamenu.hidden = true;
          }
        }, { once: true });
      }

      const listItem = trigger.closest('.js-listItem');
      listItem.classList.remove('header__nav__list-item--active');
      trigger.classList.remove('open');
      const anyOpen = Array.from(megamenus).some((menu) =>
        menu.classList.contains('header__megamenu--active')
      );

      if (!anyOpen) {
        hideOverlay();
        node.classList.remove('header--megamenu-open');
      }
    };
    let mouseoverTimeout;

    megamenuTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        clearTimeout(mouseoverTimeout);

        mouseoverTimeout = setTimeout(() => {
          megamenuTriggers.forEach((otherTrigger) => {
            const megamenu = otherTrigger.closest('.js-listItem')?.querySelector('.js-megamenu');

            if (!megamenu) return;

            if (otherTrigger === trigger) {
              if (otherTrigger.classList.contains('cannot-be-opened')) return;

              // if already open — close it
              if (otherTrigger.classList.contains('open')) {
                hideMegamenu(otherTrigger, megamenu);
              } else {
                showMegamenu(otherTrigger, megamenu);
              }
            } else {
              hideMegamenu(otherTrigger, megamenu);
            }
          });
        }, 100);
      });
    });

    document.addEventListener('click', (event) => {
      const isTrigger = event.target.closest(megamenuTriggers[0]?.tagName?.toLowerCase() || null);
      const isMegamenu = event.target.closest('.js-megamenu');
      const isLink = event.target.closest('a');

      if ((!isMegamenu && !isTrigger) || (isMegamenu && !isLink)) {
        megamenuTriggers.forEach((trigger) => {
          const megamenu = trigger.parentElement.querySelector('.js-megamenu');
          hideMegamenu(trigger, megamenu);
        });
      }
    });

    window.addEventListener('click', (event) => {
      if (event.target.tagName === 'BUTTON' && !event.target.classList.contains('product__add-to-cart-button') && !event.target.classList.contains('js-fitSizeSelectionBtn')) {
        event.preventDefault();
        return;
      }
      let closeAll = true;

      if (Array.from(megamenus).find((megamenu) => event.target === megamenu || megamenu.contains(event.target))) { // eslint-disable-line
        closeAll = false;
      }

      if (Array.from(megamenuTriggers).find((trigger) => trigger === event.target || trigger.contains(event.target))) { // eslint-disable-line
        closeAll = false;
      }
      if (event.target?.matches('a.js-cart-btn') || (event.target?.closest('a')?.matches('a.js-cart-btn'))) closeAll = false;

      if (closeAll) {
        megamenuTriggers.forEach((trigger) => {
          const megamenu = trigger.parentElement.querySelector('.js-megamenu');
          hideMegamenu(trigger, megamenu);
          if (event.target.closest('#shopify-section-announcement') || event.target.closest('header.header') && !event.target.closest('header.header button[aria-label="Menu"]')) {
            hideOverlay();
          }
        });

        node.classList.remove('header--megamenu-open');
      }
    });
  };

  const handleScroll = () => {
    const currentScrollPos = window.scrollY;
    const scrollingUp = currentScrollPos < previousScrollPos;
    previousScrollPos = currentScrollPos;

    const fixedThreshold = topbarHeight;

    if (currentScrollPos > fixedThreshold) {
      node.classList.add('header--fixed-desktop');
      document.body.classList.add('header-fixed--desktop');
    } else {
      node.classList.remove('header--fixed-desktop');
      node.classList.remove('header--transition-transform');
      document.body.classList.remove('header-fixed--desktop');
    }

    if (currentScrollPos > fixedThreshold && scrollingUp) {
      node.classList.add('header--show-desktop');
      node.classList.add('header--transition-transform');
      ctx.emit('mobileHeader:scrolled', { mobileHeaderVisible: true });
    } else {
      node.classList.remove('header--show-desktop');
      ctx.emit('mobileHeader:scrolled', { mobileHeaderVisible: false });
    }
  };


  const init = () => {
    if (topbar) {
      initTopbar();
    }

    initMegamenus();
    handleScroll();

    ctx.on('window:scroll', handleScroll);
  };

  init();
});
